import Dexie, { type Table } from "dexie";
import type { DadosEditarVistoria, VistoriaApi } from "../api";

/**
 * Fase 6 (offline-first): a fila de sincronização vive aqui, em IndexedDB via Dexie — não no
 * service worker. Background Sync API não é confiável no Safari/iOS (o alvo real do PWA: casa de
 * bombas, subsolo e elevador têm sinal ruim), então a drenagem é feita pela própria aplicação
 * React (ver offline/sync.ts), disparada por evento `online`, `visibilitychange`, polling e botão
 * manual. O service worker (vite-plugin-pwa) continua só cuidando de precache/instalabilidade.
 */

export type TipoMutacao = "resposta" | "observacao" | "campo" | "foto" | "achado_avulso";

interface MutacaoBase {
  id: string; // mutationId gerado no device (crypto.randomUUID()) — chave primária da fila
  vistoriaId: string;
  criadoEm: string; // ISO — drena nessa ordem, importante pro last-write-wins de campo/resposta
  tentativas: number;
  ultimoErro?: string;
}

export interface MutacaoResposta extends MutacaoBase {
  tipo: "resposta";
  payload: {
    secaoChave: string;
    itemChave: string;
    marcado?: boolean;
    valorEscolhido?: string;
    descricao?: string;
    problema?: boolean;
  };
}

export interface MutacaoObservacao extends MutacaoBase {
  tipo: "observacao";
  payload: { secaoChave: string; texto: string };
}

export interface MutacaoAchadoAvulso extends MutacaoBase {
  tipo: "achado_avulso";
  // itemChave gerado no device (crypto.randomUUID()) no clique de "+ Adicionar" — upsert
  // idempotente, mesma lógica de MutacaoResposta (ver PUT /:id/achados-avulsos).
  payload: { secaoChave: string; itemChave: string; descricao: string };
}

export interface MutacaoCampo extends MutacaoBase {
  tipo: "campo";
  payload: DadosEditarVistoria;
}

export interface MutacaoFoto extends MutacaoBase {
  tipo: "foto";
  // IndexedDB (e portanto Dexie) guarda Blob nativamente — sem precisar converter pra base64.
  payload: {
    arquivo: Blob;
    nomeArquivo: string;
    secaoChave: string;
    itemChave?: string;
    capturadaEmDispositivoEm?: string;
  };
}

export type MutacaoPendente = MutacaoResposta | MutacaoObservacao | MutacaoCampo | MutacaoFoto | MutacaoAchadoAvulso;

export interface VistoriaCacheEntry {
  id: string; // vistoriaId
  dados: VistoriaApi;
  atualizadoEm: string;
}

class OfflineDB extends Dexie {
  mutacoesPendentes!: Table<MutacaoPendente, string>;
  vistoriaCache!: Table<VistoriaCacheEntry, string>;

  constructor() {
    super("vistoria-offline");
    this.version(1).stores({
      mutacoesPendentes: "id, vistoriaId, criadoEm",
      vistoriaCache: "id",
    });
  }
}

export const offlineDb = new OfflineDB();
